import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { loadStripe } from "@stripe/stripe-js";
import { useAuth } from "../context/AuthContext";

const stripePromise = loadStripe("pk_live_51TblU4L0sA58YoXrYJGrrAlSkBJCgA3p63X3dXAjOEPeSeUkPwPYI1YF11NLSukbHzTzFyuFnUZLRzpaJRlt08MV00TH4KUDPG");

export default function InvoicePending() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [status, setStatus] = useState("checking");
  const [message, setMessage] = useState("Verifying your payment...");

  useEffect(() => {
    const paymentIntentId = searchParams.get("payment_intent");
    const clientSecret = searchParams.get("payment_intent_client_secret");

    if (!paymentIntentId) {
      setStatus("error");
      setMessage("Payment information missing.");
      return;
    }

    const verify = async () => {
      try {
        // Check PaymentIntent status via backend
        const res = await fetch(`https://eleven11-i6v4.onrender.com/api/payments/verify/${paymentIntentId}`);
        const data = await res.json();

        if (data.status === "succeeded" || data.status === "processing") {
          // Create order
          const cartRes = await fetch("https://eleven11-i6v4.onrender.com/api/cart");
          const cartData = await cartRes.json();
          const cartItems = (cartData.items || []).map(item => ({
            listingId: item.listingId,
            title: item.title,
            image: item.image || "",
            partNumber: item.partNumber || "",
            selectedCondition: item.selectedCondition || "",
            selectedVariant: item.selectedVariant || "",
            price: Number(item.price),
            qty: Number(item.qty || 1)
          }));

          const orderRes = await fetch("https://eleven11-i6v4.onrender.com/api/orders", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              paymentIntentId,
              userId: user?.id,
              appliedCredit: 0,
              customer: {
                firstName: user?.firstName || "",
                lastName: user?.lastName || "",
                email: user?.email || "",
                phone: user?.phone || ""
              },
              shippingAddress: {
                address: user?.shippingAddress?.address || "",
                apartment: user?.shippingAddress?.apartment || "",
                city: user?.shippingAddress?.city || "",
                state: user?.shippingAddress?.state || "",
                zip: user?.shippingAddress?.zip || "",
                country: user?.shippingAddress?.country || "United States"
              },
              items: cartItems
            })
          });

          const orderData = await orderRes.json();

          await fetch("https://eleven11-i6v4.onrender.com/api/cart/clear", { method: "DELETE" });

          if (orderData.orderId) {
            navigate(`/invoice/${orderData.orderId}`);
          } else {
            setStatus("success_no_order");
            setMessage("Payment confirmed! Your order is being processed.");
          }

        } else if (data.status === "requires_payment_method") {
          setStatus("failed");
          setMessage("Payment was not completed. Please try again.");
        } else {
          setStatus("pending");
          setMessage("Payment is still processing. Please check back shortly.");
        }

      } catch (err) {
        console.error(err);
        setStatus("error");
        setMessage("Something went wrong verifying your payment.");
      }
    };

    verify();
  }, []);

  const icon = {
    checking: "⏳",
    pending: "🔄",
    success_no_order: "✅",
    failed: "❌",
    error: "⚠️"
  }[status] || "⏳";

  const color = {
    checking: "#6366f1",
    pending: "#f59e0b",
    success_no_order: "#10b981",
    failed: "#ef4444",
    error: "#ef4444"
  }[status] || "#6366f1";

  return (
    <div style={{ minHeight: "100vh", background: "#F4F5F7", display: "flex", alignItems: "center", justifyContent: "center", padding: 24, fontFamily: "-apple-system, BlinkMacSystemFont, system-ui" }}>
      <div style={{ background: "#fff", borderRadius: 24, padding: "48px 40px", maxWidth: 440, width: "100%", textAlign: "center", boxShadow: "0 8px 40px rgba(0,0,0,0.08)" }}>

        <div style={{ fontSize: 52, marginBottom: 20 }}>{icon}</div>

        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 2, color: "#9ca3af", marginBottom: 8 }}>ELEVEN</div>

        <h2 style={{ fontSize: 24, fontWeight: 800, color: "#0f0f17", letterSpacing: -0.5, margin: "0 0 12px" }}>
          {status === "checking" ? "Verifying Payment" : status === "failed" ? "Payment Failed" : "Payment Confirmed"}
        </h2>

        <p style={{ fontSize: 14, color: "#6b7280", lineHeight: 1.6, margin: "0 0 28px" }}>{message}</p>

        {status === "checking" && (
          <div style={{ display: "flex", justifyContent: "center", gap: 6 }}>
            {[0, 1, 2].map(i => (
              <div key={i} style={{ width: 8, height: 8, borderRadius: "50%", background: color, animation: `pulse 1.2s ease-in-out ${i * 0.2}s infinite` }} />
            ))}
          </div>
        )}

        {status === "failed" && (
          <button onClick={() => navigate("/checkout")} style={{ width: "100%", padding: "14px", borderRadius: 14, border: "none", background: "#0f0f17", color: "#fff", fontSize: 15, fontWeight: 700, cursor: "pointer" }}>
            Try Again
          </button>
        )}

        {status === "pending" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <button onClick={() => window.location.reload()} style={{ width: "100%", padding: "14px", borderRadius: 14, border: "none", background: "#0f0f17", color: "#fff", fontSize: 15, fontWeight: 700, cursor: "pointer" }}>
              Check Again
            </button>
            <button onClick={() => navigate("/")} style={{ width: "100%", padding: "14px", borderRadius: 14, border: "1px solid #e5e7eb", background: "transparent", color: "#374151", fontSize: 15, fontWeight: 600, cursor: "pointer" }}>
              Back to Home
            </button>
          </div>
        )}

        {status === "success_no_order" && (
          <button onClick={() => navigate("/")} style={{ width: "100%", padding: "14px", borderRadius: 14, border: "none", background: "#10b981", color: "#fff", fontSize: 15, fontWeight: 700, cursor: "pointer" }}>
            Back to Store
          </button>
        )}

        {status === "error" && (
          <button onClick={() => navigate("/")} style={{ width: "100%", padding: "14px", borderRadius: 14, border: "none", background: "#0f0f17", color: "#fff", fontSize: 15, fontWeight: 700, cursor: "pointer" }}>
            Back to Home
          </button>
        )}

      </div>

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 0.3; transform: scale(0.8); }
          50% { opacity: 1; transform: scale(1.2); }
        }
      `}</style>
    </div>
  );
}